/**
 * ExecutorTaskList — the executor's primary settlement surface.
 *
 * §1.6 Build 1: read-only stage-grouped list. Build 2: rows expand into a drawer
 * (mark done / undo / dismiss / note / attach), done-state styling, and a merged
 * "resolved" zone per stage (auto-resolved + dismissed, distinct sub-labels).
 */
import { useState, useEffect, useRef } from "react";
import { useSearch } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import {
  Circle, CircleDot, Clock, CheckCircle2, Minus, ChevronDown, ChevronRight, Paperclip,
} from "lucide-react";
import { TaskDrawer, type DrawerTask } from "@/components/executor/TaskDrawer";
import { TaskFilterBar } from "@/components/executor/TaskFilterBar";
import { filterTasks, type FilterKey } from "@/lib/executor-task-filter";
import { PROFESSIONAL_HELP_LABEL, type ProfessionalHelpRole } from "@shared/constants/professional-help";
import { sectionNoun } from "@/components/executor/task-drawer/binding-helpers";
import { deriveAgencyBands } from "@/lib/executor-task-grouping";
import type { ResolvedDue } from "@shared/executor/due-date";
import { dueLook, todayISO } from "@shared/executor/due-look";
import { applyOptimisticTaskStatus } from "@/lib/executor-task-optimistic";
import { CLAY, FONT, SAGE, WARM } from "@shared/brand";

// ── Response shape (mirrors server/executor-task-routes.ts) ──────────────────
interface TaskRow {
  taskKey: string;
  title: string;
  guidance: string;
  consequenceTier: string;
  consequenceText: string;
  deadlineAnchor: string;
  deadlineOffsetDays: number | null;
  deadlineUnit: string | null;
  deadlineRuleText: string | null;
  gateVisible: string;
  isAutoResolved: boolean;
  autoResolveReason: string | null;
  // s231 EXECUTOR-ONBOARDING: the executor answered "Not sure" at Before you start.
  checkThis?: boolean;
  status: string;
  source: "canonical" | "custom";
  clientTaskId: string | null;
  eventCount: number;
  attachmentCount: number;
  dismissNote: string | null;
  // s126 Batch 2 enrichments (optional-guarded for older cached payloads).
  agencyKey?: string | null;
  agencyName?: string | null;
  professionalHelp?: string[];
  emptyABindings?: { section: string; sectionName: string }[];
  due?: ResolvedDue;
  /** s233 (DEADLINE-COUNTDOWN-1): the drawer's "Due 2 Oct 2026 — 30 days after the death" (server, the one calculator). */
  dueLine?: string | null;
  // s148 FORM-SEARCH-1: associated gov-form(s), so the search box matches form id/title (e.g. "74A").
  govForms?: { formId: string; title: string }[];
  // s169 Piece 2: set when this is a PROMOTED per-record task row. Sibling rows share taskKey,
  // so taskKey ALONE no longer identifies a row — see rowKey() below.
  recordRowId?: string | null;
}

/**
 * s169 Piece 2 — this list's row identity. Promoted per-record rows share their taskKey with
 * their siblings, so React keys, the open-drawer set, and testids must all key on the PAIR.
 * `taskKey` stays the API path segment; `recordRowId` rides in the mutation body alongside it.
 * Un-promoted rows keep their bare taskKey, so every existing key and testid is unchanged.
 */
function rowKey(t: TaskRow): string {
  return t.recordRowId ? `${t.taskKey}::${t.recordRowId}` : t.taskKey;
}
interface StageGroup {
  stage: number;
  label: string;
  tasks: TaskRow[];
  counts: { total: number; done: number; autoResolved: number; dismissed: number };
}
interface TaskListResponse {
  stages: StageGroup[];
  summary: { totalTasks: number; doneTasks: number; criticalRemaining: number; autoResolved: number; dismissed: number };
  plannerName: string;
  plannerCompletionCount: number;
}

const SAGE_800 = "#3A4A3C";
const CREAM = "#F7F3EA";

// [B4a-D-123.3b/4] Shared trailing-column widths so the header row (D-123.4) and
// every task row align on the same x-positions. Change here, both follow.
const COL_ATTACH = "w-11";    // fits the "Files" header label; paperclip + count right-aligned
const COL_PRIO = "w-[92px]";  // priority badge, right-aligned
const COL_DUE = "w-[104px]";  // deadline — wide enough for "Aug 30, 2026" / "After probate" on ONE line (FIX-B)

function greeting(name: string, count: number): string {
  if (count >= 8) return `${name} put a strong plan in place. Here's what needs to happen next.`;
  if (count >= 5) return `${name} recorded ${count} of 10 essentials. Here's what needs to happen next.`;
  return "Here's what needs to happen. Some information may need to be gathered along the way.";
}

// s126 item 3: the Due column shows the RESOLVED date or a SHORT placeholder — never the prose rule (that lives in the
// drawer, killing the nine-line tower). s233 (DEADLINE-COUNTDOWN-1, D-232.56): the date READS through the one
// calculator — board 12's four looks, today being the executor's own calendar day. Custom tasks keep their
// planner-entered date, still carried in deadlineRuleText.
function deadlineCell(t: TaskRow, today: string): { text: string; color: string } {
  if ((!t.due || t.due.kind === "none") && t.deadlineRuleText) return { text: t.deadlineRuleText, color: WARM.taupe };
  const look = dueLook(t.due, t.status, today); // "No fixed date" when there is none (FIX-A: never blank)
  return { text: look.text, color: look.color };
}

function StatusIcon({ status }: { status: string }) {
  const cls = "h-[18px] w-[18px] shrink-0";
  switch (status) {
    case "done": return <CheckCircle2 className={cls} style={{ color: SAGE[600] }} aria-label="Done" />;
    case "in_progress": return <CircleDot className={cls} style={{ color: CLAY[600] }} aria-label="In progress" />;
    case "waiting": return <Clock className={cls} style={{ color: WARM.taupe }} aria-label="Waiting" />;
    case "auto_resolved":
    case "dismissed": return <Minus className={cls} style={{ color: WARM.taupe }} aria-label="Resolved" />;
    default: return <Circle className={cls} style={{ color: WARM.taupe }} aria-label="Not started" />;
  }
}

// [B4a-D-123.3a / DEC-A] Three-tier client-side labelling: Critical / Important /
// Routine. `recommended` + `informational` (and any unmapped tier) collapse into the
// quiet "Routine" badge — the "nothing urgent" signal. Seed `tier` values are unchanged.
function ConsequenceBadge({ tier }: { tier: string }) {
  const map: Record<string, { bg: string; fg: string; label: string; weight: string }> = {
    critical: { bg: CLAY[600], fg: "#FFFFFF", label: "Critical", weight: "font-semibold" },
    important: { bg: SAGE[600], fg: "#FFFFFF", label: "Important", weight: "font-semibold" },
  };
  const s = map[tier] ?? { bg: WARM.stoneLight, fg: WARM.taupe, label: "Routine", weight: "font-medium" };
  return (
    <span className={`text-[11px] ${s.weight} rounded-full px-2 py-0.5 shrink-0`} style={{ backgroundColor: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function toDrawerTask(t: TaskRow): DrawerTask {
  return {
    taskKey: t.taskKey,
    recordRowId: t.recordRowId ?? null,
    guidance: t.guidance,
    consequenceText: t.consequenceText,
    deadlineRuleText: t.deadlineRuleText,
    deadlineOffsetDays: t.deadlineOffsetDays,
    deadlineUnit: t.deadlineUnit,
    dueLine: t.dueLine ?? null,
    status: t.status,
    attachmentCount: t.attachmentCount,
    professionalHelp: t.professionalHelp,
  };
}

// An active (expandable) task row.
function TaskRowView({ clientId, t, expanded, onToggle }: { clientId: string; t: TaskRow; expanded: boolean; onToggle: () => void }) {
  const dl = deadlineCell(t, todayISO());
  const isDone = t.status === "done";
  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        className="flex items-center gap-3 px-4 py-2.5 rounded-md cursor-pointer transition-colors"
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WARM.stoneLight)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        data-testid={`task-row-${rowKey(t)}`}
      >
        {/* [B4a-D-123.2] chevron moved to the LEFT, matching the planner expandable-row pattern */}
        {expanded
          ? <ChevronDown className="h-4 w-4 shrink-0" style={{ color: WARM.taupe }} />
          : <ChevronRight className="h-4 w-4 shrink-0" style={{ color: WARM.taupe }} />}
        <StatusIcon status={t.status} />
        <div className="flex-1 min-w-0">
          <div className="text-[15px] leading-snug truncate" style={{ color: WARM.charcoal, textDecoration: isDone ? "line-through" : "none", opacity: isDone ? 0.6 : 1 }}>
            {t.title}
          </div>
          {(t.checkThis || (t.professionalHelp?.length ?? 0) > 0 || (t.emptyABindings?.length ?? 0) > 0) && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              {/* s231 EXECUTOR-ONBOARDING (board: "Added to your list, marked 'Check this'"). */}
              {t.checkThis && (
                <span className="inline-flex items-center text-[11px] rounded-full px-2 py-0.5"
                  style={{ backgroundColor: CLAY[100], color: CLAY[800] }}
                  data-testid={`task-check-this-${rowKey(t)}`}>
                  Check this
                </span>
              )}
              {/* item 8: recommendation pill per role (coexists with WhoCanHelp cards in the drawer) */}
              {t.professionalHelp?.map((role) => (
                <span key={role} className="inline-flex items-center text-[11px] rounded-full px-2 py-0.5"
                  style={{ backgroundColor: CREAM, color: SAGE_800, border: `1px solid ${WARM.stone}` }}
                  data-testid={`task-help-${rowKey(t)}-${role}`}>
                  {PROFESSIONAL_HELP_LABEL[role as ProfessionalHelpRole] ?? role}
                </span>
              ))}
              {/* item 6: empty A-binding hint on the collapsed ribbon (drawer stays precise) */}
              {t.emptyABindings?.map((b) => (
                <span key={b.section} className="text-[11px]" style={{ color: WARM.taupe }}
                  data-testid={`task-empty-${rowKey(t)}-${b.section}`}>
                  No {sectionNoun(b.section, b.sectionName)} recorded
                </span>
              ))}
            </div>
          )}
        </div>
        {/* [B4a-D-123.3b] fixed trailing columns — attachment · priority · due — so badges,
            counts, and dates line up on the same x-position across every row (+ the header). */}
        <div className={`${COL_ATTACH} flex justify-end shrink-0`}>
          {t.attachmentCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] tabular-nums" style={{ color: WARM.taupe }}
              title={`${t.attachmentCount} attachment${t.attachmentCount === 1 ? "" : "s"}`}>
              <Paperclip className="h-3 w-3" />{t.attachmentCount}
            </span>
          )}
        </div>
        <div className={`${COL_PRIO} flex justify-end shrink-0`}>
          <ConsequenceBadge tier={t.consequenceTier} />
        </div>
        <div className={`${COL_DUE} text-right text-[12px] tabular-nums whitespace-nowrap shrink-0`} style={{ color: dl.color }}
          data-testid={`task-due-${rowKey(t)}`}>{dl.text}</div>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${WARM.stone}` }}>
          <TaskDrawer clientId={clientId} task={toDrawerTask(t)} />
        </div>
      )}
    </div>
  );
}

function RestoreButton({ clientId, taskKey, recordRowId }: { clientId: string; taskKey: string; recordRowId?: string | null }) {
  const qc = useQueryClient();
  const tasksKey = [`/api/executor/${clientId}/tasks`];
  // Same synchronous interlock as the drawer actions: `disabled={isPending}` only engages after a
  // re-render, so clicks inside one tick would each fire a real restore.
  //
  // 🔴 s170 BEHAVIOUR CORRECTION (SINGLE-FLIGHT-PRIMITIVE-1). This was the second hand-rolled copy
  // of the s169 pattern, and it released the lock from `onSettled` — the variant the drawer's own
  // comment documents as rejected, because `isPending` can clear before that callback runs, leaving
  // the button visibly live while the lock still swallows the click. Moving to the shared primitive
  // moves the release onto the mutation PROMISE, so the disabled window and the lock window are the
  // same window. The background refetch still fires without being awaited.
  const restoreFlight = useSingleFlight();
  const restore = useMutation({
    // s169 Piece 2: recordRowId rides in the BODY. The path segment is the taskKey, which
    // sibling promoted rows share — without this the server would restore an arbitrary one.
    mutationFn: async () =>
      (await apiRequest("POST", `/api/executor/${clientId}/tasks/${taskKey}/restore`, { recordRowId: recordRowId ?? null })).json(),
    // s169 TASK-ACTION-FEEDBACK-1: same optimistic + hold-pending-through-refetch contract as the
    // drawer actions. A restore moves the row OUT of the resolved zone, so without optimism the
    // row sits visibly unchanged until the refetch — the same dead window that caused stacking.
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: tasksKey });
      const previous = qc.getQueryData(tasksKey);
      if (previous) {
        qc.setQueryData(tasksKey, applyOptimisticTaskStatus(previous as any, taskKey, recordRowId ?? null, "not_started"));
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous !== undefined) qc.setQueryData(tasksKey, ctx.previous); },
    // The refresh is fired WITHOUT awaiting — holding the button disabled through the background
    // refetch left it dead for seconds (Test 2 fail). cancelQueries in onMutate is what keeps a
    // late refetch from clobbering the optimistic state. The lock itself is released by
    // `useSingleFlight` when the mutation promise settles, not here (s170).
    onSettled: () => { void qc.invalidateQueries({ queryKey: tasksKey }); },
  });
  return (
    <button type="button"
      onClick={() => restoreFlight.runQuiet(() => restore.mutateAsync())}
      disabled={restoreFlight.inFlight || restore.isPending}
      className="text-[12px] font-medium shrink-0 disabled:opacity-60 disabled:cursor-not-allowed" style={{ color: SAGE[600] }}
      data-testid={`task-restore-${recordRowId ? `${taskKey}::${recordRowId}` : taskKey}`}>
      {restore.isPending ? "Restoring…" : "Restore"}
    </button>
  );
}

// A resolved (non-active) row: auto-resolved OR dismissed. Sub-labels stay verbally
// distinct so the executor always knows which were their own call (Reade guard).
function ResolvedRow({ clientId, t, searching }: { clientId: string; t: TaskRow; searching?: boolean }) {
  const dismissed = !t.isAutoResolved && t.status === "dismissed";
  return (
    <div className="flex items-center gap-3 px-4 py-2" style={{ opacity: 0.6 }} data-testid={`resolved-row-${rowKey(t)}`}>
      <Minus className="h-[18px] w-[18px] shrink-0" style={{ color: WARM.taupe }} />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] leading-snug truncate" style={{ color: WARM.charcoal }}>{t.title}</div>
        <div className="text-[12px] truncate" style={{ color: WARM.taupe }}>
          {dismissed
            ? `Dismissed by you${t.dismissNote ? ` · ${t.dismissNote}` : ""}`
            : `Auto-resolved — ${t.autoResolveReason ?? "based on the estate plan"}`}
        </div>
      </div>
      {/* D-130.7: when a search surfaces a resolved match, tag it so its state is obvious. */}
      {searching && (
        <span className="shrink-0 text-[11px] font-medium rounded-full px-2 py-0.5" style={{ backgroundColor: CREAM, color: WARM.taupe, border: `1px solid ${WARM.stone}` }} data-testid={`resolved-tag-${rowKey(t)}`}>
          Doesn't apply
        </span>
      )}
      {dismissed && <RestoreButton clientId={clientId} taskKey={t.taskKey} recordRowId={t.recordRowId} />}
    </div>
  );
}

// Collapsible per-stage "resolved" zone (auto-resolved + dismissed), quiet by default.
// D-130.7: auto-expanded while searching so a resolved match isn't hidden behind a collapsed toggle.
function ResolvedZone({ clientId, tasks, searching }: { clientId: string; tasks: TaskRow[]; searching?: boolean }) {
  const [open, setOpen] = useState(false);
  const expanded = open || !!searching;
  const auto = tasks.filter((t) => t.isAutoResolved).length;
  const dismissed = tasks.filter((t) => !t.isAutoResolved && t.status === "dismissed").length;
  const parts: string[] = [];
  if (auto) parts.push(`${auto} auto-resolved`);
  if (dismissed) parts.push(`${dismissed} dismissed by you`);
  return (
    <div style={{ borderTop: `1px dashed ${WARM.stone}` }} className="mt-1 pt-1">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left px-4 py-2 text-[13px]" style={{ color: WARM.taupe }} data-testid="resolved-zone-toggle">
        {expanded ? "▾" : "▸"} {parts.join(" · ")}
      </button>
      {expanded && tasks.map((t) => <ResolvedRow key={rowKey(t)} clientId={clientId} t={t} searching={searching} />)}
    </div>
  );
}

function Pill({ label, color, active, onClick }: { label: string; color: string; active?: boolean; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[13px] font-medium rounded-full px-3 py-1 transition-shadow cursor-pointer"
      style={{ backgroundColor: CREAM, color: WARM.charcoal, border: `1px solid ${WARM.stone}`, boxShadow: active ? `0 0 0 2px ${SAGE[600]}` : "none" }}
      aria-pressed={active}>
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </button>
  );
}

// [B4a-D-123.4] Column headers, repeated inside each stage group directly above that
// stage's first task row. Muted 13px DM Sans (the brand "Muted" token). Spacers mirror
// the row's chevron + status-icon leading zone so every label sits over its fixed column.
function TaskColumnHeader() {
  return (
    <div className="flex items-center gap-3 px-4 pb-1.5" data-testid="task-column-header">
      <span className="h-4 w-4 shrink-0" aria-hidden />
      <span className="h-[18px] w-[18px] shrink-0" aria-hidden />
      <div className="flex-1 min-w-0 text-[13px]" style={{ color: WARM.taupe }}>Task</div>
      <div className={`${COL_ATTACH} text-right text-[13px] shrink-0`} style={{ color: WARM.taupe }}>Files</div>
      <div className={`${COL_PRIO} text-right text-[13px] shrink-0`} style={{ color: WARM.taupe }}>Priority</div>
      <div className={`${COL_DUE} text-right text-[13px] whitespace-nowrap shrink-0`} style={{ color: WARM.taupe }}>Due</div>
    </div>
  );
}

function ActiveTasks({ clientId, tasks, openKeys, toggle }: {
  clientId: string; tasks: TaskRow[]; openKeys: Set<string>; toggle: (k: string) => void;
}) {
  const rowFor = (t: TaskRow) => (
    <TaskRowView key={rowKey(t)} clientId={clientId} t={t} expanded={openKeys.has(rowKey(t))} onToggle={() => toggle(rowKey(t))} />
  );
  return (
    <>
      {deriveAgencyBands(tasks).map((seg, idx) =>
        seg.kind === "band" ? (
          <div key={`band-${seg.agencyKey}-${idx}`} className="my-1 rounded-md"
            style={{ backgroundColor: "#F6F3EC", borderLeft: `2px solid ${WARM.stone}` }}
            data-testid={`agency-band-${seg.agencyKey}`}>
            <div className="px-4 pt-2 pb-0.5 text-[12px] font-medium" style={{ color: SAGE_800 }}>{seg.agencyName}</div>
            {seg.tasks.map(rowFor)}
          </div>
        ) : (
          <div key={`loose-${idx}`}>{seg.tasks.map(rowFor)}</div>
        ),
      )}
    </>
  );
}

function StageGroupView({ clientId, group, narrowing, searching, autoOpenTaskKey }: { clientId: string; group: StageGroup; narrowing: boolean; searching?: boolean; autoOpenTaskKey?: string }) {
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const toggle = (k: string) => setOpenKeys((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  // F-4 (s133): while a search is active this stage always renders EXPANDED — the parent only
  // renders stages that HAVE ≥1 match, so a manually-collapsed stage must not hide its match.
  // Derived (not a state write), so the manual `collapsed` state is restored when the query clears.
  const effectiveCollapsed = collapsed && !searching;

  // Deep-link (s124 Fix 5): when linked with ?task=<taskKey> (e.g. from the notes widget's
  // "re: [task]" line), the group that owns that task expands its drawer once and scrolls
  // the row into view. Once-per-param ref so a later manual collapse isn't fought.
  const appliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoOpenTaskKey || appliedRef.current === autoOpenTaskKey) return;
    if (!group.tasks.some((t) => t.taskKey === autoOpenTaskKey)) return;
    appliedRef.current = autoOpenTaskKey;
    setCollapsed(false);
    setOpenKeys((prev) => new Set(prev).add(autoOpenTaskKey));
    // Scroll once the row/drawer has rendered — a couple of rAF ticks is enough.
    let raf = 0;
    let tries = 0;
    const tick = () => {
      const el = document.querySelector(`[data-testid="task-row-${autoOpenTaskKey}"]`);
      if (el) { el.scrollIntoView({ block: "center" }); return; }
      if (++tries < 30) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoOpenTaskKey, group.tasks]);

  const active = group.tasks.filter((t) => !t.isAutoResolved && t.status !== "dismissed");
  const resolved = group.tasks.filter((t) => t.isAutoResolved || t.status === "dismissed");

  // Under any narrowing (filter or search) the server-side counts no longer describe
  // what's visible, so show a plain matched count; otherwise the full progress header.
  const headerText = narrowing
    ? `${group.tasks.length} ${group.tasks.length === 1 ? "task" : "tasks"}`
    : `${group.counts.done} of ${group.counts.total} done${resolved.length > 0 ? ` · ${resolved.length} resolved` : ""}`;

  // s126 item 2: Stage 7 renders as an UNNUMBERED "Ongoing" band (duties that run the
  // whole administration), pinned at the bottom by the ascending stage order.
  const isOngoing = group.stage === 7;

  return (
    <section className="mb-4">
      <button type="button" onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: CREAM, border: `1px solid ${WARM.stone}` }}
        data-testid={`stage-header-${group.stage}`}>
        {effectiveCollapsed ? <ChevronRight className="h-4 w-4 shrink-0" style={{ color: SAGE_800 }} /> : <ChevronDown className="h-4 w-4 shrink-0" style={{ color: SAGE_800 }} />}
        <div className="flex flex-col items-start min-w-0">
          <span style={{ fontFamily: FONT.serif, fontSize: 18, fontWeight: 500, color: SAGE_800 }}>
            {isOngoing ? "Ongoing — throughout the estate" : `Stage ${group.stage} — ${group.label}`}
          </span>
          {isOngoing && (
            <span className="text-[12px] leading-snug text-left" style={{ color: WARM.taupe }}>
              These duties start on day one and continue until the estate closes.
            </span>
          )}
        </div>
        <span className="ml-auto text-[13px] shrink-0" style={{ color: WARM.taupe }}>{headerText}</span>
      </button>

      {!effectiveCollapsed && (
        <div className="mt-2 py-1.5 rounded-lg" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }}>
          {active.length > 0 && <TaskColumnHeader />}
          <ActiveTasks clientId={clientId} tasks={active} openKeys={openKeys} toggle={toggle} />
          {resolved.length > 0 && <ResolvedZone clientId={clientId} tasks={resolved} searching={searching} />}
        </div>
      )}
    </section>
  );
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-6 w-2/3 rounded mb-4" style={{ backgroundColor: WARM.stone }} />
      <div className="h-8 w-1/2 rounded mb-6" style={{ backgroundColor: WARM.stone }} />
      {[1, 2, 3].map((i) => (
        <div key={i} className="mb-4">
          <div className="h-12 rounded-lg mb-2" style={{ backgroundColor: CREAM }} />
          <div className="h-24 rounded-lg" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }} />
        </div>
      ))}
    </div>
  );
}

export default function ExecutorTaskList({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery<TaskListResponse>({
    queryKey: [`/api/executor/${clientId}/tasks`],
    enabled: !!clientId,
  });
  const [filter, setFilter] = useState<FilterKey>("All");
  const [query, setQuery] = useState("");

  // Deep-link target (s124 Fix 5): ?task=<taskKey> from the notes widget. Reset any active
  // filter/search once per param so the target task is guaranteed visible before the group
  // auto-opens its drawer (below). Reactive via wouter's useSearch.
  const searchStr = useSearch();
  const autoOpenTaskKey = new URLSearchParams(searchStr).get("task") ?? undefined;
  const appliedParamRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoOpenTaskKey || appliedParamRef.current === autoOpenTaskKey) return;
    appliedParamRef.current = autoOpenTaskKey;
    setFilter("All");
    setQuery("");
  }, [autoOpenTaskKey]);

  const narrowing = filter !== "All" || query.trim() !== "";
  const visibleStages = (data?.stages ?? [])
    .map((s) => ({ ...s, tasks: filterTasks(s.tasks, filter, query) }))
    .filter((s) => s.tasks.length > 0);

  const emptyMessage = (): string => {
    if (query.trim()) return `No tasks match "${query.trim()}".`;
    switch (filter) {
      case "Waiting": return "Nothing is waiting on others right now.";
      case "Done": return "Nothing marked done yet.";
      case "Critical": return "No critical tasks remaining.";
      case "To do": return "Nothing to do right now.";
      default: return "No tasks found.";
    }
  };

  return (
    <div className="min-h-full" style={{ backgroundColor: CREAM }}>
      <div className="max-w-[960px] mx-auto px-6 py-8">
        {isLoading || !data ? (
          <LoadingSkeleton />
        ) : (
          <>
            <h1 style={{ fontFamily: FONT.serif, fontSize: 24, fontWeight: 500, color: WARM.charcoal }} className="mb-2" data-testid="task-list-greeting">
              {greeting(data.plannerName, data.plannerCompletionCount)}
            </h1>

            <div className="flex flex-wrap gap-2 mb-5 mt-4">
              <Pill label={`${data.summary.doneTasks} done`} color={SAGE[600]} active={filter === "Done"} onClick={() => setFilter("Done")} />
              <Pill label={`${data.summary.totalTasks - data.summary.doneTasks} remaining`} color={WARM.taupe} active={filter === "To do"} onClick={() => setFilter("To do")} />
              <Pill label={`${data.summary.criticalRemaining} critical`} color={CLAY[600]} active={filter === "Critical"} onClick={() => setFilter("Critical")} />
            </div>

            <TaskFilterBar filter={filter} onFilter={setFilter} query={query} onQuery={setQuery} />

            {visibleStages.length === 0 ? (
              <p className="text-center text-[15px] py-12" style={{ color: WARM.taupe }} data-testid="task-empty-state">{emptyMessage()}</p>
            ) : (
              visibleStages.map((group) => <StageGroupView key={group.stage} clientId={clientId} group={group} narrowing={narrowing} searching={query.trim() !== ""} autoOpenTaskKey={autoOpenTaskKey} />)
            )}
          </>
        )}
      </div>
    </div>
  );
}
